'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildQuerySummary } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useMemo, useRef } from 'react';
import { interpolateStringWithData } from 'utils';
import xss from 'xss';
import { useEngine } from '../EngineContext';
import { QuerySummarySkeleton } from '../Skeleton/QuerySummarySkeleton';
import { hasSearchQuery } from '../hooks/hasSearchQuery';
import { QuerySummaryProps } from './QuerySummary.types';

const TITLE_MICROCOPY_KEY = 'search.page-title';
const CATEGORY_FALLBACK_KEY = 'search.page-title-category-fallback';

/**
 * Read the `q` parameter from the URL hash fragment as a fallback when the
 * Coveo QuerySummary controller does not expose `state.query` (this can
 * happen on deep-link navigations where the URL manager restores search
 * parameters but the controller state is not yet updated).
 */
function getQueryFromHash(): string {
  if (typeof window === 'undefined') return '';
  try {
    return new URLSearchParams(window.location.hash.slice(1)).get('q') ?? '';
  } catch {
    return '';
  }
}

export const QuerySummary = ({
  variant = 'full',
  className,
}: QuerySummaryProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const eng = useEngine();
  const controller = useMemo(() => buildQuerySummary(eng), [eng]);
  const state = useController(controller);
  const hasSearched = useRef(false);

  if (state.firstSearchExecuted) {
    hasSearched.current = true;
  }

  const getMicrocopy = (key: string, fallback: string) => {
    const entry = searchLabelsMicrocopy?.find((m) => m.key === key);
    return entry?.value ?? fallback;
  };

  if (!state.hasResults && hasSearched.current) {
    return null;
  }

  // Show skeleton only during initial load, before the first search
  if (!state.firstSearchExecuted) {
    // Title variant only renders when there's a search query — skip skeleton
    // if no q= in the URL hash to avoid a layout shift when it disappears
    if (variant === 'title' && !hasSearchQuery()) return null;
    return <QuerySummarySkeleton variant={variant} className={className} />;
  }

  if (!state.hasResults) {
    return null;
  }

  const resultCountText = `Results ${state.firstResult}\u2013${state.lastResult} of ${state.total}`;

  // Build search title — sanitized with the xss library to prevent XSS
  const searchQuery = state.query || getQueryFromHash();
  const titleTemplate = getMicrocopy(
    TITLE_MICROCOPY_KEY,
    '{{search}} in {{category}}'
  );
  const categoryFallback = getMicrocopy(
    CATEGORY_FALLBACK_KEY,
    'All categories'
  );

  let sanitizedTitle: string | null = null;
  if (searchQuery) {
    const title = interpolateStringWithData(titleTemplate, {
      search: searchQuery,
      category: categoryFallback,
    });
    // xss() sanitizes HTML to prevent injection attacks
    sanitizedTitle = xss(title);
  }

  if (variant === 'title') {
    if (!sanitizedTitle) return null;
    return (
      <h1
        className={className}
        data-test="search-page-title"
        dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
      />
    );
  }

  if (variant === 'count') {
    return (
      <p className={className} data-test="query-summary-count">
        {resultCountText}
      </p>
    );
  }

  // Full variant (default) — used on mobile
  return (
    <div className={className} data-test="query-summary">
      {sanitizedTitle && (
        <h1
          className="header-mid mb-4 font-normal lg:mb-6"
          data-test="search-page-title"
          dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
        />
      )}
      <p className="body-small text-grey-600" data-test="query-summary-count">
        {resultCountText}
      </p>
    </div>
  );
};
